import { useMemo, useEffect, useState } from 'react';

interface SnowEffectProps {
  intensity?: 'light' | 'medium' | 'heavy';
}

const SnowEffect = ({ intensity = 'medium' }: SnowEffectProps) => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const baseCount = intensity === 'light' ? 30 : intensity === 'medium' ? 60 : 100;
  const snowflakeCount = isMobile ? Math.floor(baseCount * 0.5) : baseCount;

  const snowflakes = useMemo(() => {
    return Array.from({ length: snowflakeCount }, (_, i) => ({
      id: i,
      left: Math.random() * 100,
      size: Math.random() * 6 + 2,
      animationDuration: Math.random() * 5 + 5,
      animationDelay: Math.random() * 5,
      opacity: Math.random() * 0.6 + 0.4,
    }));
  }, [snowflakeCount]);

  return (
    <div 
      className="fixed inset-0 pointer-events-none z-40 overflow-hidden"
      style={{ height: '100dvh', minHeight: '100vh' }}
    >
      <style>
        {`
          @keyframes snowfall {
            0% {
              transform: translateY(-10px) translateX(0) rotate(0deg) translateZ(0);
              opacity: 1;
            }
            25% {
              transform: translateY(25dvh) translateX(10px) rotate(90deg) translateZ(0);
            }
            50% {
              transform: translateY(50dvh) translateX(-5px) rotate(180deg) translateZ(0);
            }
            75% {
              transform: translateY(75dvh) translateX(15px) rotate(270deg) translateZ(0);
            }
            100% {
              transform: translateY(100dvh) translateX(5px) rotate(360deg) translateZ(0);
              opacity: 0.3;
            }
          }
          
          @media (prefers-reduced-motion: reduce) {
            .snowflake {
              animation: none !important;
              opacity: 0 !important;
            }
          }
        `}
      </style>
      {snowflakes.map((flake) => (
        <div
          key={flake.id}
          className="snowflake absolute rounded-full bg-white"
          style={{
            left: `${flake.left}%`,
            width: `${flake.size}px`,
            height: `${flake.size}px`,
            opacity: flake.opacity,
            animation: `snowfall ${flake.animationDuration}s linear infinite`,
            animationDelay: `${flake.animationDelay}s`,
            boxShadow: '0 0 4px rgba(255, 255, 255, 0.8)',
            willChange: 'transform, opacity',
            backfaceVisibility: 'hidden',
          }}
        />
      ))}
    </div>
  );
};

export default SnowEffect;
